import { ChallengeTypeConfig } from 'api/types'
import { HCP_MAX_TENTHS, HCP_MIN_TENTHS } from 'utils/hcpTenths'
import { ChallengeComputedState, ChallengeControlledState, ChallengeInfoContentType } from 'utils/challenges'
import { UserSubscriptionType } from 'utils/users'
import * as yup from 'yup'

const contentSchema = yup.object().shape({
  type: yup
    .mixed<ChallengeInfoContentType>()
    .oneOf(Object.values(ChallengeInfoContentType))
    .default(ChallengeInfoContentType.CHALLENGE_INFO_CONTENT_TYPE_RICH_TEXT)
    .required('Content type is required'),
  content: yup.string().required('Content is required').default(''),
})

const optionalContentSchema = yup
  .object()
  .shape({
    type: yup
      .mixed<ChallengeInfoContentType>()
      .oneOf(Object.values(ChallengeInfoContentType))
      .default(ChallengeInfoContentType.CHALLENGE_INFO_CONTENT_TYPE_RICH_TEXT)
      .required('Content type is required'),
    content: yup.string().required('Content is required').default(''),
  })
  .optional()
  .default(undefined)

// Number inputs hand over an empty string when they are cleared, which casts to NaN. Optional
// numbers have to become undefined instead, otherwise validation fails on every untouched field.
const optionalNumberSchema = yup
  .number()
  .transform((value, originalValue) =>
    originalValue === '' || originalValue === null || isNaN(value) ? undefined : value,
  )
  .notRequired()
  .default(undefined)

// Unlike the other content, opt-in text may be empty while the opt-in toggle is off
const termsDescriptionSchema = yup.object().shape({
  type: yup
    .mixed<ChallengeInfoContentType>()
    .oneOf(Object.values(ChallengeInfoContentType))
    .default(ChallengeInfoContentType.CHALLENGE_INFO_CONTENT_TYPE_RICH_TEXT)
    .notRequired(),
  content: yup.string().notRequired().default(''),
})

const prizesSchema = yup.object().shape({
  type: yup
    .mixed<ChallengeInfoContentType>()
    .oneOf(Object.values(ChallengeInfoContentType))
    .default(ChallengeInfoContentType.CHALLENGE_INFO_CONTENT_TYPE_RICH_TEXT)
    .notRequired(),
  content: yup.string().notRequired().default(''),
})

const prizeInfoSchema = yup.object({
  hasPrizes: yup.boolean().default(false),
  isRequired: yup.boolean().notRequired().default(false),

  shortDescription: prizesSchema.when('hasPrizes', {
    is: true,
    then: (schema) =>
      schema.shape({
        content: yup.string().required('Short description required'),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),

  longDescription: prizesSchema.when('hasPrizes', {
    is: true,
    then: (schema) =>
      schema.shape({
        content: yup.string().required('Long description required'),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
})

const birthdateSchema = yup.object().shape({
  birthdateLowerLimit: yup.string().notRequired(),
  birthdateUpperLimit: yup.string().notRequired(),
})

// A handicap limit is stored in tenths, so the accepted range of +10.0 to 54.0 is 100 to -540.
const hcpTenthsSchema = optionalNumberSchema
  .integer('Handicap allows one decimal at most')
  .min(HCP_MIN_TENTHS, 'Handicap has to be between +10.0 and 54.0')
  .max(HCP_MAX_TENTHS, 'Handicap has to be between +10.0 and 54.0')

const hcpSchema = yup
  .object()
  .shape({
    minTenths: hcpTenthsSchema,
    maxTenths: hcpTenthsSchema,
  })
  .test('hcp-range-order', 'Lowest HCP has to be lower than highest HCP', (value) => {
    if (typeof value?.minTenths !== 'number' || typeof value?.maxTenths !== 'number') {
      return true
    }

    return value.minTenths <= value.maxTenths
  })

const selectOptionSchema = yup.object().shape({
  key: yup
    .mixed<string | number>()
    .required('Key is required')
    .test(
      'is-string-or-number',
      'Key must be a string or number',
      (value) => typeof value === 'string' || typeof value === 'number',
    ),
  label: yup.string().required('Label is required'),
})

const audienceSchema = yup.object().shape({
  countries: yup.array().of(selectOptionSchema).required('Countries are required').default([]),
  countriesInclusive: yup.boolean().required('Countries inclusive setting is required').default(true),
  birthdateFilter: birthdateSchema.required('Birthdate filter is required'),
  hcpFilter: hcpSchema.notRequired(),
  gender: yup.string().notRequired(),
  privileges: yup.array().of(selectOptionSchema).notRequired().default([]),
  clubIds: yup.array().of(selectOptionSchema).required('Club IDs are required').default([]),
  userSubscriptionTypes: yup
    .array()
    .of(yup.mixed<UserSubscriptionType>().oneOf(Object.values(UserSubscriptionType)))
    .notRequired()
    .default([]),
})

export const challengeSchema = yup
  .object()
  .shape({
    id: yup.string().default('-1'),
    controlledState: yup.string().default(ChallengeControlledState.DRAFT),
    computedState: yup.string().default(ChallengeComputedState.UNSPECIFIED),
    info: yup
      .object()
      .shape({
        name: yup.string().required('Type & scheduling: Name is required'),
        subtitle: contentSchema.required('Descriptions: Subtitle is required'),
        longDescription: contentSchema.required('Descriptions: Long description is required'),
        additionalInformation: optionalContentSchema,
      })
      .required(),
    typeConfig: yup
      .object()
      .shape({
        type: yup
          .mixed<(typeof ChallengeTypeConfig)[keyof typeof ChallengeTypeConfig]>()
          .oneOf(
            [
              ChallengeTypeConfig.NUMBER_OF_HOLES_PLAYED,
              ChallengeTypeConfig.NUMBER_OF_COURSES_PLAYED,
              ChallengeTypeConfig.NUMBER_OF_PARS,
              ChallengeTypeConfig.BIRDIES_OR_BETTER,
              ChallengeTypeConfig.STROKEPLAY,
              ChallengeTypeConfig.STABLEFORD,
              ChallengeTypeConfig.ECLECTIC,
            ],
            'Type & scheduling: Type is required',
          )
          .required('Type & scheduling: Type is required'),
        target: optionalNumberSchema,
        birdiesOrBetter: yup
          .object()
          .shape({
            birdieValue: yup.number().required('Birdie value is required'),
            eagleValue: yup.number().required('Eagle value is required'),
            hioValue: yup.number().required('Hole-in-one value is required'),
            albatrossValue: yup.number().required('Albatross value is required'),
          })
          .notRequired()
          .default(undefined),
        strokeplay: yup
          .object()
          .shape({
            useHandicap: yup.boolean().required(),
          })
          .notRequired()
          .default(undefined),
        stableford: yup
          .object()
          .shape({
            resultAggregation: yup.string().required(),
          })
          .notRequired()
          .default(undefined),
        eclectic: yup
          .object()
          .shape({
            courseIds: yup.array(yup.string()).required(),
          })
          .notRequired()
          .default(undefined),
      })
      .required(),
    schedule: yup
      .object()
      .shape({
        visibleAt: yup.string().required('Visible at time is required'),
        startAt: yup.string().required('Start time is required'),
        endAt: yup.string().required('End time is required'),
      })
      .required(),
    audienceControl: yup.object().shape({
      isDiscoverable: yup.boolean().required('Audience: Is discoverable flag is required').default(true),
      useJoinAudienceOnly: yup.boolean().required('Audience: Use join audience only flag is required').default(true),
      joinAudience: audienceSchema.notRequired().default({
        countries: undefined,
        countriesInclusive: true,
        birthdateFilter: undefined,
        hcpFilter: undefined,
        privileges: undefined,
        clubIds: undefined,
        userSubscriptionTypes: [],
      }),
      extraViewAudience: audienceSchema.notRequired().default({
        countries: undefined,
        countriesInclusive: true,
        birthdateFilter: undefined,
        hcpFilter: undefined,
        privileges: undefined,
        clubIds: undefined,
        userSubscriptionTypes: [],
      }),
    }),
    roundEligibility: yup
      .object()
      .shape({
        clubIds: yup.array(selectOptionSchema).default(undefined),
        courseIds: yup.array(selectOptionSchema).default(undefined),
        countries: yup.array(selectOptionSchema).default(undefined),
        countriesInclusive: yup
          .boolean()
          .required('Type & scheduling: Location country includes must be either "includes" or "excludes"')
          .default(true),
        minCoursePar: optionalNumberSchema.min(1).max(99),
        roundTypes: yup.array().of(yup.string().required()).default([]),
      })
      .required(),
    media: yup.object().shape({
      coverPreset: yup.string(),
      coverImageId: yup.string(),
      highlightImageId: yup.string(),
      bannerImageId: yup.string(),
      logoImageId: yup.string(),
      backgroundImageId: yup.string(),
      termsImageId: yup.string(),
    }),
    optInRequired: yup.boolean().notRequired().default(false),
    // The opt-in toggle decides whether terms are used at all, so without it terms may be
    // missing, null or empty. The description is only demanded once the toggle is on.
    terms: yup
      .object()
      .shape({
        description: termsDescriptionSchema,
        isRequired: yup.boolean().notRequired().default(false),
      })
      .nullable()
      .optional()
      .default(undefined),
    prizeInfo: prizeInfoSchema.default(undefined),
    metadata: yup
      .object()
      .shape({
        timeZone: yup.string().optional(),
      })
      .optional()
      .default(undefined),
    participantsStats: yup
      .object()
      .shape({
        count: yup.number().optional(),
      })
      .optional()
      .default(undefined),
  })
  .test('courseIds check', 'Select exactly one course to make this challenge eligible.', function (value) {
    if (value.typeConfig.type !== ChallengeTypeConfig.ECLECTIC) {
      return true
    }

    return value.roundEligibility.courseIds && value.roundEligibility.courseIds.length === 1
  })
  .test('terms-description-required', 'Opt-in: Text is required', function (value) {
    if (!value?.terms?.isRequired) {
      return true
    }

    if (!value.terms?.description?.content?.trim()) {
      return this.createError({
        path: 'terms.description.content',
        message: 'Opt-in: Text is required',
      })
    }

    return true
  })
  .test('countries exclusive needs a country', 'Exclude type: needs atleast one country', function (value) {
    if (value.roundEligibility.countriesInclusive || value.roundEligibility.countries?.length !== 0) {
      return true
    }

    const challengeHasStarted = !!value?.schedule?.startAt && new Date(value.schedule.startAt).getTime() <= Date.now()

    const computedState = this.options.context?.computedState ?? ChallengeComputedState.UNSPECIFIED

    if (!challengeHasStarted || computedState === ChallengeComputedState.DRAFT) {
      return this.createError({
        path: 'round eligibility',
        message: 'Round eligibility type exclusive needs atleast one country',
      })
    }

    return this.createError({
      path: 'round eligibility',
      message:
        computedState === ChallengeComputedState.UNSPECIFIED
          ? 'Round eligibility type exclusive needs atleast one country'
          : 'Round eligibility type exclusive needs atleast one country, Refresh page to undo latest changes.',
    })
  })
  .test(`start time can't earlier than vibsible time`, 'Start time is set before visible time', function (value) {
    if (new Date(value.schedule.visibleAt) <= new Date(value.schedule.startAt)) {
      return true
    }

    return this.createError({
      path: 'start time',
      message: `start time can't be earlier than challenge visibility time`,
    })
  })
